import React, { useContext, useEffect, useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import { cloneDeep, isNull } from 'lodash'
import OutsideClickHandler from 'react-outside-click-handler'
import MarblesChart from '../components/MarblesChart'
import { ArrowDownIcon } from '../components/Icons'
import generateRiskCounts from '../ultils/generateRiskCounts'
import { Context as PortfolioContext } from '../contexts/portfolio'
import {
  creditRiskStatusData,
  filterByRiskType,
  mappingMarbleData,
  RISK_STATUS_COLOR_SCALE,
  RISK_TYPE,
} from '../ultils/chart-d3-utils'
import { mappingTitleSearch } from '../ultils/dataUtil'
import PortfolioTableMobile from '../components/PortfolioTableMobile'

const useStyles = makeStyles({
  visArea: {
    width: '100%',
  },
  indexBoard: {
    width: `200px`,
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #DFE4EB',
    borderRadius: 5,
  },
  legendWrapper: {
    width: '300px',
    height: '500px',
    transformOrigin: 'center',
    transform: 'scale(1.2)',
    position: 'relative',
    left: '14px',
  },
  marbleHead: {
    padding: 16,
    boxSizing: 'border-box',
    display: 'block',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  marbleCompanyAmount: {
    marginBottom: 10,
  },
  marbleCompanyDropdown: {},
  marbleCompanyValue: {
    fontSize: 20,
    lineHeight: '24px',
    fontWeight: 400,
    color: '#181818',
  },
  marbleCompanyTitle: {
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: 600,
    color: '#181818',
  },
  MarbleDropdown: {
    position: 'relative',
  },
  MarbleDropdownHead: {
    height: '45px',
    background: '#F4F5F7',
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    paddingLeft: 12,
    paddingRight: 12,
  },
  MarbleDropdownCaret: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  MarbleDropdownCircle: {
    width: 18,
    height: 18,
    boxSizing: 'border-box',
    borderRadius: '100%',
    background: '#A3CCFB',
    border: '2px solid #1D82F9',
  },
  MarbleDropdownCaretText: {
    fontSize: 12,
    lineHeight: '22px',
    fontWeight: 400,
    color: '#1B3963',
    marginLeft: 8,
  },
  ArrowDownIcon: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  MarbleDropdownContent: {
    position: 'absolute',
    width: '100%',
    right: 0,
    top: 48,
    height: 225,
    borderRadius: 3,
    background: '#F4F5F7',
  },
  MarbleDropdownCircleHigh: {
    background: '#D312124D',
    border: '2px solid #D31212',
  },
  MarbleDropdownCircleMedium: {
    background: '#ED5F5F33',
    border: '2px solid #EC5151',
  },
  MarbleDropdownCircleLow: {
    background: '#E391934D',
    border: '2px solid #E8A6A7',
  },
  MarbleDropdownCircleNeutral: {
    background: '#BBC0C74D',
    border: '2px solid #BBBFC7',
  },
  marbleCompanyLineChart: {
    marginTop: 20,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  MarbleLineChart: {},
  lineChartInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  lineChartInfoLeft: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  lineChartInfoLeftTitlte: {
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: 600,
    color: '#474747',
    marginRight: 4,
  },
  lineChartInfoLeftValue: {
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: 600,
    color: '#474747',
  },
  lineChartInfoRight: {
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: 400,
    color: '#474747',
  },
  lineChartProgress: {
    height: 4,
    width: '100%',
    borderRadius: 4,
    background: '#E8EBEF',
  },
  MarbleLineChartEachLine: {
    marginBottom: 16,
  },
})

const MarbleDropdown = ({ activeNode, getValue, tickers, riskType }) => {
  const classes = useStyles()
  const [toggleDropdown, setToggleDropdown] = useState(false)
  const [activeItem, setActiveItem] = useState('Total')
  const onToggleDropdown = () => {
    setToggleDropdown(!toggleDropdown)
  }
  const onClickItemDropdown = item => {
    setActiveItem(item)
    setToggleDropdown(false)
    // getValue
    mappingMarbleData(item, getValue)
  }
  const getActiveCircle = val => {
    if (val === 'Total') {
      return classes['MarbleDropdownCircle']
    }
    if (val === 'High') {
      return classes['MarbleDropdownCircleHigh']
    }
    if (val === 'Medium') {
      return classes['MarbleDropdownCircleMedium']
    }
    if (val === 'Low') {
      return classes['MarbleDropdownCircleLow']
    }
    if (val === 'Fire') {
      return classes['MarbleDropdownCircleHigh']
    }
    if (val === 'Smoke') {
      return classes['MarbleDropdownCircleMedium']
    }
    if (val === 'Watchlist') {
      return classes['MarbleDropdownCircleLow']
    }
    if (val === 'Neutral') {
      return classes['MarbleDropdownCircleNeutral']
    }
    return ''
  }

  const getActiveCircleLabel = val => {
    let mappingTitle = ''
    switch (val) {
      case 'Fire':
        mappingTitle = `Fire`
        break
      case 'Smoke':
        mappingTitle = `Smoke`
        break
      case 'Watchlist':
        mappingTitle = `Watchlist`
        break
      case 'High':
      case 'Medium':
      case 'Low':
        mappingTitle = `Watchlist`
        break
      default:
        mappingTitle = val
        break
    }
    return mappingTitle
  }

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setToggleDropdown(false)
      }}
    >
      <div className={classes.MarbleDropdown}>
        <div onClick={onToggleDropdown} className={classes.MarbleDropdownHead}>
          <div className={classes.MarbleDropdownCaret}>
            <div
              className={`${classes.MarbleDropdownCircle} ${getActiveCircle(
                activeItem,
              )}`}
            />
            <div className={classes.MarbleDropdownCaretText}>
              {getActiveCircleLabel(activeItem)}
            </div>
          </div>
          <div className={classes.ArrowDownIcon}>
            <ArrowDownIcon />
          </div>
        </div>

        {toggleDropdown && (
          <div className={classes.MarbleDropdownContent}>
            <div
              onClick={() => onClickItemDropdown('Total')}
              className={classes.MarbleDropdownHead}
            >
              <div className={classes.MarbleDropdownCaret}>
                <div className={`${classes.MarbleDropdownCircle}`} />
                <div className={classes.MarbleDropdownCaretText}>Total</div>
                <div className={classes.MarbleDropdownCaretText}>
                  [{tickers.length}]
                </div>
              </div>
            </div>
            <div
              onClick={() => onClickItemDropdown('Fire')}
              className={classes.MarbleDropdownHead}
            >
              <div className={classes.MarbleDropdownCaret}>
                <div
                  className={`${classes.MarbleDropdownCircle} ${classes.MarbleDropdownCircleHigh}`}
                />
                <div className={classes.MarbleDropdownCaretText}>Fire</div>
                <div className={classes.MarbleDropdownCaretText}>
                  [
                  {
                    tickers.filter(item =>
                      filterByRiskType(item, riskType, 'Fire'),
                    ).length
                  }
                  ]
                </div>
              </div>
            </div>
            <div
              onClick={() => onClickItemDropdown('Smoke')}
              className={classes.MarbleDropdownHead}
            >
              <div className={classes.MarbleDropdownCaret}>
                <div
                  className={`${classes.MarbleDropdownCircle} ${classes.MarbleDropdownCircleMedium}`}
                />
                <div className={classes.MarbleDropdownCaretText}>Smoke</div>
                <div className={classes.MarbleDropdownCaretText}>
                  [
                  {
                    tickers.filter(item =>
                      filterByRiskType(item, riskType, 'Smoke'),
                    ).length
                  }
                  ]
                </div>
              </div>
            </div>
            <div
              onClick={() => onClickItemDropdown('Watchlist')}
              className={classes.MarbleDropdownHead}
            >
              <div className={classes.MarbleDropdownCaret}>
                <div
                  className={`${classes.MarbleDropdownCircle} ${classes.MarbleDropdownCircleLow}`}
                />
                <div className={classes.MarbleDropdownCaretText}>Watchlist</div>
                <div className={classes.MarbleDropdownCaretText}>
                  [
                  {
                    tickers.filter(item =>
                      filterByRiskType(item, riskType, 'Watchlist'),
                    ).length
                  }
                  ]
                </div>
              </div>
            </div>
            <div
              onClick={() => onClickItemDropdown('Neutral')}
              className={classes.MarbleDropdownHead}
            >
              <div className={classes.MarbleDropdownCaret}>
                <div
                  className={`${classes.MarbleDropdownCircle} ${classes.MarbleDropdownCircleNeutral}`}
                />
                <div className={classes.MarbleDropdownCaretText}>Neutral</div>
                <div className={classes.MarbleDropdownCaretText}>
                  [
                  {
                    tickers.filter(item =>
                      filterByRiskType(item, riskType, 'Neutral'),
                    ).length
                  }
                  ]
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  )
}

const MarbleLineChart = ({ tickers, riskType }) => {
  const classes = useStyles()
  const listLevel = creditRiskStatusData.map(l => l.value)

  return (
    <>
      {listLevel.map(level => (
        <div className={classes.MarbleLineChart}>
          <div className={classes.MarbleLineChartEachLine}>
            <div className={classes.lineChartInfo}>
              <div className={classes.lineChartInfoLeft}>
                <div className={classes.lineChartInfoLeftTitlte}>
                  {mappingTitleSearch(level)}
                </div>
                <div className={classes.lineChartInfoLeftValue}>
                  (
                  {
                    tickers.filter(item =>
                      filterByRiskType(item, riskType, level),
                    ).length
                  }
                  )
                </div>
              </div>
              <div className={classes.lineChartInfoRight}>
                {Math.round(
                  (tickers.filter(item =>
                    filterByRiskType(item, riskType, level),
                  ).length *
                    100) /
                    tickers.length,
                )}
                %
              </div>
            </div>
            <div className={classes.lineChartProgress}>
              <div
                style={{
                  width: `${
                    (tickers.filter(item =>
                      filterByRiskType(item, riskType, level),
                    ).length *
                      100) /
                    tickers.length
                  }%`,
                  height: '100%',
                  background: RISK_STATUS_COLOR_SCALE[level],
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

const iniFilter = {
  sector: [],
  industry: [],
  region: [],
  country: [],
  searchByWord: '',
}

const PortfolioTodayMobile = ({ tickers, riskType }) => {
  const classes = useStyles()

  const riskCountData = generateRiskCounts(tickers, riskType)
  const { state, setActiveNode } = useContext(PortfolioContext)

  const [selectedFilter, setSelectedFilter] = useState(iniFilter)

  const baseFiltered = useMemo(() => {
    if (
      isNull(state.activeNode) ||
      state.activeNode.name === 'total' ||
      !state.activeNode.name
    )
      return cloneDeep(tickers)

    return cloneDeep(tickers).filter(t =>
      riskType === RISK_TYPE.CREDIT
        ? t.credit_status === state.activeNode.name
        : t.risk_outlook === state.activeNode.name,
    )
  }, [state.activeNode, tickers, riskType])

  const tickerData = useMemo(() => {
    let tmp = baseFiltered
    if (selectedFilter?.sector && selectedFilter?.sector.length > 0) {
      tmp = tmp.filter(t =>
        selectedFilter?.sector.includes(t.factset_sector_desc),
      )
    }
    if (selectedFilter?.industry && selectedFilter?.industry.length > 0) {
      tmp = tmp.filter(t => selectedFilter?.industry.includes(t.industry))
    }
    if (selectedFilter?.region && selectedFilter?.region.length > 0) {
      tmp = tmp.filter(t => selectedFilter?.region.includes(t.region))
    }
    if (selectedFilter?.country && selectedFilter?.country.length > 0) {
      tmp = tmp.filter(t => selectedFilter?.country.includes(t.iso_country))
    }
    if (selectedFilter?.searchByWord?.trim()) {
      const searchLower = selectedFilter.searchByWord.trim().toLowerCase()
      tmp = tmp.filter(
        t =>
          t.proper_name?.toLowerCase().includes(searchLower) ||
          t.ticker?.toLowerCase().includes(searchLower),
      )
    }
    return tmp
  }, [baseFiltered, selectedFilter])

  const setMarblesData = d => {
    setActiveNode(d)
  }

  const resetAllFilters = React.useCallback(() => {
    setSelectedFilter(iniFilter)
    setActiveNode(null)
  }, [])

  useEffect(() => {
    setSelectedFilter(iniFilter)
  }, [state.activeNode])

  return (
    <>
      <div className={classes.chartContainer}>
        <div className={classes.visArea}>
          <div className={classes.marbleHead}>
            <div className={classes.marbleCompanyAmount}>
              <span className={classes.marbleCompanyValue}>
                {tickerData.length}{' '}
              </span>
              <span className={classes.marbleCompanyTitle}>Companies</span>
            </div>

            <div className={classes.marbleCompanyDropdown}>
              <MarbleDropdown
                activeNode={state.activeNode}
                getValue={val => setMarblesData(val)}
                tickers={tickers}
                riskType={riskType}
              />
            </div>
          </div>
          <MarblesChart
            isEmptyData={state.tickersInPortfolio.length === 0}
            tickers={tickerData}
            riskType={riskType}
            group="none"
          />
          <div className={classes.marbleCompanyLineChart}>
            <MarbleLineChart tickers={tickers} riskType={riskType} />
          </div>
        </div>
      </div>
      <div id="portfolioTable">
        <PortfolioTableMobile
          tickers={tickerData}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          onResetAll={resetAllFilters}
          // tickerId={tickerId}
          activeNode={state.activeNode}
        />
      </div>
    </>
  )
}

export default PortfolioTodayMobile
